import type {CodexNotification} from '@reados/contracts';
import {operationSummary} from './operation-summary';
import {turnLifecycles} from './turn-lifecycle.ts';
import {transcriptTimeText} from './assistant-footer.ts';
type Value = Record<string, unknown>;
const object = (v:unknown):Value => v && typeof v==='object' && !Array.isArray(v) ? v as Value : {};
const string = (v:unknown) => typeof v==='string' ? v : '';
const identity=(message:Value)=>JSON.stringify([message.threadId,message.readexCodexTurnID]);
const presentationIdentity=(message:Value)=>string(message.readexWebTurnPresentationID)||identity(message);
const summaryParts=(block:Value)=>{
  const parts=Array.isArray(block.summaryParts) ? block.summaryParts.map(string).filter(value=>value.trim()) : [];
  return parts.length ? parts : string(block.text).trim() ? [string(block.text)] : [];
};

/** Browser port of ReadexTranscriptTurnPayloadBuilder's assistant segment and
 * processing-block ownership. It changes view DTOs only, never stored messages,
 * original DIL source, citation offsets or upstream task state.
 */
export function readexTurnLayout(messages:Value[], blocks:Value[], events:CodexNotification[]) {
  const now = Date.now();
  const byBlock=new Map(blocks.map(block=>[block.id,block]));
  const turns=turnLifecycles(events);
  const projected:Value[]=[],catalog:Value[]=[];
  for(const message of messages){
    const previous=projected.at(-1);
    if(message.role==='assistant' && previous?.role==='assistant' && identity(message)===identity(previous)){
      (previous.blocks as unknown[]).push(...message.blocks as unknown[]);
      previous.status=message.status==='error'||previous.status==='error' ? 'error' : message.status==='streaming'||previous.status==='streaming' ? 'streaming' : 'success';
      if (previous.readexTurnStartedAtMilliseconds === undefined && typeof message.readexTurnStartedAtMilliseconds === 'number')
        previous.readexTurnStartedAtMilliseconds = message.readexTurnStartedAtMilliseconds;
      if (typeof message.branchNoticeText === 'string' && message.branchNoticeText.trim())
        previous.branchNoticeText = message.branchNoticeText;
    } else projected.push({...message,renderHarness:'readex',blocks:[...message.blocks as unknown[]]});
  }
  // Native items arrive separately, but the assistant segment is the stable
  // view owner. Its identity must survive turn/started -> first reasoning/tool
  // item; the original renderer retains this DOM group as its reading anchor.
  // A steering user message creates the next segment within the same turn.
  const segmentOrdinals=new Map<string,number>();
  for(const message of projected){
    if(message.role!=='assistant')continue;
    const key=presentationIdentity(message),ordinal=segmentOrdinals.get(key)||0;
    segmentOrdinals.set(key,ordinal+1);
    // DIL/Markdown persist by patchKey + source block key. Keep the original
    // segment owner there; only the enclosing group uses the stable view ID.
    message.patchKey=message.id;
    message.id=`reados-assistant:${key}:${ordinal}`;
  }
  // The canonical preceding user segment owns each assistant preview. Native
  // thread/turn identities prevent an orphan or another agent borrowing a
  // nearby user's content; stable presentation IDs survive optimistic sends.
  const precedingUsers=new Map<string,string>();
  for(const message of projected){
    if(message.role==='user'||message.role==='steered'){
      precedingUsers.set(identity(message),string(message.id));
    } else if(message.role==='assistant'){
      const replyTo=precedingUsers.get(identity(message));
      if(replyTo)message.replyToMessageID=replyTo;
      else delete message.replyToMessageID;
    }
  }
  const lastAssistant=new Map<string,Value>(),publicSummaries=new Set<string>(),finalTexts=new Set<string>(),chromeOwners=new Set<string>();
  // Build operation sources in transcript order. iPad emits the summary at the
  // first non-empty final answer, so later steering segments must not move it
  // or add newly-started operations to an already-emitted summary.
  const operationSources=new Map<string,Value[]>(),operationPlans=new Map<string,{owner:Value;sources:Value[]}>();
  for(const message of projected){
    if(message.role!=='assistant')continue;
    const key=identity(message);lastAssistant.set(key,message);
    for(const id of message.blocks as unknown[]){
      const block=byBlock.get(id);if(!block)continue;
      if(block.type==='thinking' && summaryParts(block).length)publicSummaries.add(key);
      if(block.type==='readex_tool_activity'&&Array.isArray(block.items)&&!operationPlans.has(key))operationSources.set(key,[...(operationSources.get(key)||[]),...block.items as Value[]]);
      if(block.type==='main_text'&&block.phase==='final_answer'&&string(block.text).trim()){
        finalTexts.add(key);
        const sources=operationSources.get(key)||[];
        if(!operationPlans.has(key)&&operationSummary(key,sources))operationPlans.set(key,{owner:message,sources:[...sources]});
      }
    }
  }
  for(const message of projected){
    const input=(message.blocks as unknown[]).map(id=>byBlock.get(id)).filter((block):block is Value=>!!block);
    const output:Value[]=[],key=identity(message);
    const foldGroup=`readex-processing:${presentationIdentity(message)}`;
    const turn=turns.get(key);
    // Native lifecycle survives replay and bounded forks. ChatMessage.createdAt
    // is not an authoritative item timestamp in the Web projection.
    message.timeText=transcriptTimeText(turn?.startedAtMilliseconds
      ?? (typeof message.readexTurnStartedAtMilliseconds==='number' ? message.readexTurnStartedAtMilliseconds : undefined), now);
    const running=turn ? turn.status==='inProgress' : message.status==='streaming';
    const completed=turn ? turn.status!=='inProgress' : message.status==='success';
    const startedAt=turn?.startedAtMilliseconds ?? message.readexTurnStartedAtMilliseconds;
    // Item completion is not turn completion. The original summary renderer
    // intentionally reads the assistant segment lifecycle first.
    if(message.role==='assistant' && turn)message.status=running ? 'streaming' : turn.status==='failed'||turn.status==='interrupted' ? 'error' : 'success';
    let processing:Value|undefined,runOrdinal=0;
    const ensureProcessing=(original:Value={})=>{
      if(processing)return processing;
      const continuation=chromeOwners.has(key);
      const id=continuation ? `${message.id}:readex_processing:continuation:${runOrdinal++}` : `${foldGroup}:owner`;
      processing={...original,id,type:'readex_processing',messageID:message.id,items:[],
        status:running ? 'processing' : turn?.status==='interrupted' ? 'interrupted' : message.status==='error' ? 'failed' : 'completed',
        readexProcessingActive:running,readexProcessingGroupID:foldGroup,readexProcessingFoldGroupID:foldGroup,
        ...(continuation ? {readexProcessingChromeRole:'continuation'} : {}),
        ...(typeof startedAt==='number' ? {startedAtMilliseconds:startedAt,readexTurnStartedAtMilliseconds:startedAt}:{}),
        ...(typeof turn?.durationMilliseconds==='number' ? {durationMilliseconds:turn.durationMilliseconds,readexTurnDurationMilliseconds:turn.durationMilliseconds}:{}),
      };
      output.push(processing);chromeOwners.add(key);return processing;
    };
    // A pending submission and turn/started both render before the first model
    // item. One stable owner also remains when that first item later arrives.
    if(message.role==='assistant' && !chromeOwners.has(key) && (turn || typeof startedAt==='number'))ensureProcessing();
    for(const original of input){
      if(message.role==='assistant' && original.type==='readex_tool_activity'){
        const items=Array.isArray(original.items)?original.items:[];
        const stopped=turn?.status==='interrupted'||turn?.status==='failed';
        const presented=stopped?items.map(value=>{
          const item=object(value);
          if(item.readexNativeItemType!=='commandExecution'||item.status!=='running')return value;
          // The invocation has no completion witness. End only its live view;
          // original native status/output/exitCode remain in messages/events.
          // External MCP work and child agents have independent lifecycles.
          return {...item,status:'interrupted',completed:true,readexTerminalState:'unresolved',text:'资料库命令未完成',
            commandExecution:{...object(item.commandExecution),status:'interrupted'}};
        }):items;
        (ensureProcessing(original).items as unknown[]).push(...presented);
        continue;
      }
      const block:Value={...original,messageID:message.id};
      if(message.role==='assistant' && block.type==='thinking'){
        block.type='reasoning_summary';block.summaryParts=summaryParts(block);
        // iPad appendReasoning omits empty public summaries. Otherwise every
        // empty native reasoning item creates another generic thinking row.
        if(!(block.summaryParts as unknown[]).length)continue;
        if(!chromeOwners.has(key))ensureProcessing();
        block.status=running ? 'streaming' : message.status==='error' ? 'error' : 'success';
        if(completed)block.readexProcessingFoldGroupID=foldGroup;
      }
      if(message.role==='assistant' && block.type==='main_text' && block.phase==='commentary'){
        if(!chromeOwners.has(key))ensureProcessing();
        // Original WebBundle requires text_segment identity to join the fold.
        block.id=`${message.id}:text_segment:${original.sourceBlockId || original.id}`;
        block.readexProcessingFoldGroupID=foldGroup;
      }
      output.push(block);processing=undefined;
    }
    if(message.role==='assistant' && running && !chromeOwners.has(key) && !output.some(block=>block.type==='readex_context_status')){
      const owner=ensureProcessing();
      output.unshift(...output.splice(output.indexOf(owner),1));
    }
    if(message.role==='assistant' && running && lastAssistant.get(key)===message && !publicSummaries.has(key) && !finalTexts.has(key) && !output.some(block=>block.type==='readex_context_status')){
      // ReadexTranscriptProcessingLifecycle keeps one initial-thinking activity
      // at the work tail until public summary or the first final-answer text.
      const owner=ensureProcessing();
      const id=`readex-chat-thinking:${presentationIdentity(message)}`;
      (owner.items as unknown[]).push({id,sourceBlockId:id,type:'progress',server:'codex',text:'正在思考',status:'processing',completed:false,
        ...(typeof startedAt==='number' ? {startedAtMilliseconds:startedAt}:{})});
    }
    const reasoning=output.filter(block=>block.type==='reasoning_summary');
    const finals=output.filter(block=>block.type==='main_text'&&block.phase==='final_answer');
    const operationPlan=operationPlans.get(key);
    if(message.role==='assistant'&&operationPlan?.owner===message){
      const summary=operationSummary(key,operationPlan.sources,turn?.completedAtMilliseconds);
      if(summary){
        const lastWork=[...output].reverse().find(block=>block.type==='readex_processing');
        if(lastWork&&!(completed&&publicSummaries.has(key)))(lastWork.items as unknown[]).push(summary);
        else{
          processing=undefined;
          const owner=ensureProcessing();
          owner.id=`${foldGroup}:operation-summary`;owner.items=[summary];
        }
      }
    }
    const operationContinuations=output.filter(block=>block.type==='readex_processing'&&block.readexProcessingChromeRole==='continuation'
      &&Array.isArray(block.items)&&block.items.length>0&&block.items.every(item=>object(item).type==='operation_summary'));
    // Running public reasoning is an independent work-tail row; completed
    // reasoning joins the owner's fold. Whole final-answer/DIL stays outside.
    const ordered=reasoning.length&&completed
      ? [...output.filter(block=>!reasoning.includes(block)&&!finals.includes(block)&&!operationContinuations.includes(block)),...reasoning,...operationContinuations,...finals]
      : [...output.filter(block=>!reasoning.includes(block)&&!finals.includes(block)),...reasoning,...finals];
    message.blocks=ordered.map(block=>block.id);catalog.push(...ordered);
  }
  return {messages:projected,blockCatalog:catalog,messageGroups:projected.map(message=>({id:`group:${message.id}`,role:message.role,messageIDs:[message.id]}))};
}
